import React, { useEffect, useId, useState } from 'react';
import { Button, Form, Icon, Message, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import {
  defaultRetrySettingsV1021,
  retryCategoriesV1021,
  retryFieldsV1021,
  retryTextV1021,
  validRetrySettingsV1021,
} from './retryPolicyV1021';
import './retryControlsV1021.css';
import './keyFallbackV107.css';
import KeySettingsDisclosureV1022 from '../Token/KeySettingsDisclosureV1022';

export function RetryHelpV1021({ label, children }) {
  return (
    <Popup
      className='key-help-popup-v107 retry-help-popup-v1021'
      content={children}
      on={['hover', 'focus', 'click']}
      position='top center'
      popperModifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            padding: 12,
            rootBoundary: 'viewport',
            altAxis: true,
            tether: false,
          },
        },
      ]}
      trigger={
        <button type='button' className='key-help-v107' aria-label={label}>
          <Icon name='info circle' />
        </button>
      }
    />
  );
}

export function RetryCategoryControlsV1021({
  settings,
  onChange,
  disabled = false,
  fallbackAllowed = true,
  onValidityChange,
}) {
  const { i18n } = useTranslation();
  const text = retryTextV1021(i18n?.resolvedLanguage || i18n?.language);
  const id = useId();
  const [invalid, setInvalid] = useState({});
  const hasInvalid = Object.values(invalid).some(Boolean);
  useEffect(() => {
    onValidityChange?.(!hasInvalid);
  }, [hasInvalid, onValidityChange]);

  const change = (category, field, value) => {
    const key = `${category}-${field}`;
    const valid = /^[0-5]$/.test(value);
    setInvalid((current) => ({ ...current, [key]: !valid }));
    if (valid) {
      onChange({
        ...settings,
        [category]: { ...settings[category], [field]: Number(value) },
      });
    }
  };

  return (
    <div className='retry-categories-v1021'>
      {retryCategoriesV1021.map((category) => (
        <section
          key={category}
          className='retry-category-v1021'
          aria-labelledby={`${id}-${category}`}
          data-retry-category={category}
        >
          <h3 id={`${id}-${category}`}>{text[category]}</h3>
          {retryFieldsV1021.map((field) => {
            const key = `${category}-${field}`;
            const inputID = `${id}-${key}`;
            const blocked = field === 'fallback_retries' && !fallbackAllowed;
            return (
              <div key={field} className='retry-count-v1021'>
                <Form.Input
                  id={inputID}
                  label={text[field]}
                  aria-label={`${text[category]} ${text[field]}`}
                  aria-invalid={Boolean(invalid[key])}
                  aria-describedby={
                    invalid[key] ? `${inputID}-error` : undefined
                  }
                  type='number'
                  inputMode='numeric'
                  min={0}
                  max={5}
                  step={1}
                  required
                  disabled={disabled || blocked}
                  value={settings?.[category]?.[field] ?? ''}
                  error={Boolean(invalid[key])}
                  onFocus={(event) => event.target.select()}
                  onChange={(_, data) => change(category, field, data.value)}
                />
                {blocked && (
                  <span className='retry-boundary-v1021'>{text.blocked}</span>
                )}
                {invalid[key] && (
                  <span
                    id={`${inputID}-error`}
                    role='alert'
                    className='retry-error-v1021'
                  >
                    {text.range}
                  </span>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

export function KeyRetryPolicyV1021({ policy, onChange }) {
  const { i18n } = useTranslation();
  const text = retryTextV1021(i18n?.resolvedLanguage || i18n?.language);
  const [resetVersion, setResetVersion] = useState(0);
  const custom = policy.retry_by_category != null;
  const legacy = !custom && policy.retry_limit != null;
  const fallbackAllowed = policy.source_fallback_enabled !== false;
  const enabled = policy.retry_enabled !== false;
  const valid =
    !custom ||
    (policy.retry_limit == null &&
      validRetrySettingsV1021(policy.retry_by_category));
  const reset = () => {
    const {
      retry_limit: _legacy,
      retry_by_category: _custom,
      ...retained
    } = policy;
    setResetVersion((value) => value + 1);
    onChange({ ...retained, retry_by_category: defaultRetrySettingsV1021() });
  };

  return (
    <section className='retry-policy-v1021' aria-label={text.title}>
      <div className='retry-billing-v117'>
        <p>{text.billing}</p>
        <p>{text.billingBasis}</p>
        <p>{text.imageDefaults}</p>
        {!enabled && <p role='status'>{text.masterOff}</p>}
      </div>
      <KeySettingsDisclosureV1022
        name='retry'
        title={text.title}
        summary={enabled ? text.enabled : text.disabled}
        actions={
          <Popup
            content={text.toggleHelp}
            trigger={
              <Form.Checkbox
                toggle
                role='switch'
                aria-label={text.toggle}
                aria-checked={enabled}
                checked={enabled}
                onChange={(_, { checked }) =>
                  onChange({ ...policy, retry_enabled: checked === true })
                }
              />
            }
          />
        }
      >
        <p className='retry-defaults-v117'>{text.defaults}</p>
        <div className='retry-heading-v1021'>
          <RetryHelpV1021 label={text.help}>
            <div>
              <p>{text.sequence}</p>
              <p>{text.safety}</p>
            </div>
          </RetryHelpV1021>
          {!legacy && (
            <Popup
              content={text.reset}
              trigger={
                <Button
                  type='button'
                  basic
                  icon='undo'
                  aria-label={text.reset}
                  className='retry-reset-v1021'
                  onClick={reset}
                  disabled={!enabled}
                />
              }
            />
          )}
        </div>
        <div className='retry-permission-v1021'>
          <Form.Checkbox
            toggle
            label={text.allowFallback}
            aria-label={text.allowFallback}
            checked={enabled && fallbackAllowed}
            disabled={!enabled}
            onChange={(_, data) =>
              onChange({
                ...policy,
                source_fallback_enabled: data.checked === true,
              })
            }
          />
          <RetryHelpV1021 label={text.permissionHelp}>
            {text.permission}
          </RetryHelpV1021>
        </div>
        {legacy ? (
          <div className='retry-legacy-actions-v1021'>
            <span>
              {text.legacyCount}: <strong>{policy.retry_limit}</strong>
            </span>
            <RetryHelpV1021 label={text.legacy}>
              {text.legacyHelp}
            </RetryHelpV1021>
            <Button
              type='button'
              basic
              icon='exchange'
              content={text.upgrade}
              onClick={reset}
              disabled={!enabled}
            />
          </div>
        ) : !valid ? (
          <Message negative>
            <p>{text.invalid}</p>
          </Message>
        ) : (
          <RetryCategoryControlsV1021
            key={resetVersion}
            settings={policy.retry_by_category || defaultRetrySettingsV1021()}
            fallbackAllowed={enabled && fallbackAllowed}
            disabled={!enabled}
            onChange={(settings) =>
              onChange({ ...policy, retry_by_category: settings })
            }
          />
        )}
      </KeySettingsDisclosureV1022>
    </section>
  );
}
